import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPenFancy, FaClock, FaCheckCircle, FaArrowRight } from "react-icons/fa";

const ToeflWritingInstructions = ({ mock = 1 }) => {
    const navigate = useNavigate();

    // Height of the fixed navbar (measured dynamically)
    const [topOffset, setTopOffset] = useState(110);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Hide site footer so the page fits exactly in one screen
    useEffect(() => {
        const footer = document.querySelector("footer");
        if (footer) footer.style.display = "none";
        return () => {
            if (footer) footer.style.display = "";
        };
    }, []);

    // Measure the navbar so content never hides underneath it
    useEffect(() => {
        const nav = document.querySelector("header") || document.querySelector("nav");

        const measure = () => {
            if (!nav) return;
            const style = window.getComputedStyle(nav);
            const isOverlay =
                style.position === "fixed" ||
                style.position === "sticky" ||
                style.position === "absolute";
            if (isOverlay) {
                const bottom = nav.getBoundingClientRect().bottom;
                setTopOffset(Math.max(Math.ceil(bottom), 0) + 8);
            } else {
                setTopOffset(8);
            }
        };

        measure();
        const t = setTimeout(measure, 300);
        window.addEventListener("resize", measure);

        let ro;
        if (nav && typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(nav);
        }

        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", measure);
            if (ro) ro.disconnect();
        };
    }, []);

    return (
        <div className="tw-page" style={{ paddingTop: topOffset }}>
            <style>{`
                .tw-page {
                    height: 100vh;
                    box-sizing: border-box;
                    background: #ffffff;
                    color: #111827;
                    padding-left: 20px;
                    padding-right: 20px;
                    padding-bottom: 16px;
                    font-family: 'Inter', sans-serif;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    overflow: hidden;
                }
                .tw-inner {
                    width: 100%;
                    max-width: 1000px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    gap: clamp(0.8rem, 2.6vh, 1.9rem);
                }

                /* Header */
                .tw-header { text-align: center; }
                .tw-icon {
                    width: clamp(56px, 8.5vh, 88px);
                    height: clamp(56px, 8.5vh, 88px);
                    background: #ecfdf5;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto clamp(0.5rem, 1.5vh, 1.1rem);
                    border: 1px solid #d1fae5;
                    color: #19fd91;
                    font-size: clamp(1.5rem, 4.2vh, 2.4rem);
                }
                .tw-title {
                    font-size: clamp(1.6rem, 4.6vh, 2.4rem);
                    font-weight: 700;
                    margin: 0 0 clamp(0.4rem, 1.2vh, 0.9rem);
                    color: #166534;
                }
                .tw-subtitle {
                    color: #6b7280;
                    font-size: clamp(1rem, 2.3vh, 1.2rem);
                    max-width: 680px;
                    margin: 0 auto;
                    line-height: 1.55;
                }

                /* Info cards */
                .tw-cards {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: clamp(0.9rem, 2.4vh, 1.6rem);
                }
                .tw-card {
                    background: #ffffff;
                    padding: clamp(1rem, 2.6vh, 1.7rem) 1.5rem;
                    border-radius: 18px;
                    border: 1px solid #e5e7eb;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                }
                .tw-card-head {
                    display: flex;
                    align-items: center;
                    gap: 0.9rem;
                    margin-bottom: 0.7rem;
                    color: #19fd91;
                }
                .tw-card-head h3 {
                    margin: 0;
                    color: #111827;
                    font-size: clamp(1.1rem, 2.4vh, 1.35rem);
                }
                .tw-card p {
                    color: #4b5563;
                    margin: 0;
                    line-height: 1.55;
                    font-size: clamp(0.95rem, 2.1vh, 1.1rem);
                }

                /* Table */
                .tw-table-wrap {
                    background: #ffffff;
                    border-radius: 18px;
                    border: 1px solid #e5e7eb;
                    overflow: hidden;
                    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                }
                .tw-table {
                    width: 100%;
                    border-collapse: collapse;
                    color: #4b5563;
                    font-size: clamp(0.95rem, 2.1vh, 1.1rem);
                }
                .tw-table th {
                    padding: clamp(0.8rem, 1.8vh, 1.2rem) 1.4rem;
                    text-align: left;
                    color: #111827;
                    background: #f9fafb;
                    border-bottom: 1px solid #e5e7eb;
                }
                .tw-table td {
                    padding: clamp(0.8rem, 1.8vh, 1.2rem) 1.4rem;
                    line-height: 1.55;
                }
                .tw-table td.tw-task-name {
                    font-weight: 600;
                    color: #111827;
                    white-space: nowrap;
                    width: 1%;
                }

                /* Button */
                .tw-btn-wrap { text-align: center; }
                .tw-btn {
                    background: #19fd91;
                    color: #000;
                    border: none;
                    padding: clamp(0.85rem, 2vh, 1.15rem) 3.2rem;
                    font-size: clamp(1.05rem, 2.4vh, 1.3rem);
                    font-weight: 700;
                    border-radius: 50px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    transition: transform 0.2s;
                    box-shadow: 0 4px 15px rgba(25, 253, 145, 0.25);
                }
                .tw-btn:hover { transform: scale(1.05); }

                /* Tablets / phones: stack cards and allow normal scrolling */
                @media (max-width: 700px) {
                    .tw-page {
                        height: auto;
                        min-height: 100vh;
                        overflow: visible;
                        justify-content: flex-start;
                        padding-bottom: 32px;
                    }
                    .tw-cards { grid-template-columns: 1fr; }
                    .tw-table td.tw-task-name { white-space: normal; }
                }

                /* Very short laptop screens: tighten a bit */
                @media (max-height: 700px) and (min-width: 701px) {
                    .tw-subtitle { line-height: 1.35; }
                    .tw-card p, .tw-table td { line-height: 1.35; }
                }
            `}</style>

            <div className="tw-inner">
                {/* Header */}
                <div className="tw-header">
                    <div className="tw-icon">
                        <FaPenFancy />
                    </div>
                    <h1 className="tw-title">TOEFL Writing Test</h1>
                    <p className="tw-subtitle">
                        TOEFL iBT (2026 format): build sentences, write an email and contribute
                        to an academic discussion.
                    </p>
                </div>

                {/* Info Cards */}
                <div className="tw-cards">
                    <div className="tw-card">
                        <div className="tw-card-head">
                            <FaClock size={26} />
                            <h3>Time Limit</h3>
                        </div>
                        <p>
                            About <strong>23 minutes</strong> in total. Each task has its own timer and
                            moves on automatically when time runs out.
                        </p>
                    </div>

                    <div className="tw-card">
                        <div className="tw-card-head">
                            <FaCheckCircle size={26} />
                            <h3>Task</h3>
                        </div>
                        <p>
                            You will complete <strong>three tasks</strong>. Your section score is reported on
                            the <strong>1–6 band</strong> scale.
                        </p>
                    </div>
                </div>

                {/* Task Table */}
                <div className="tw-table-wrap">
                    <table className="tw-table">
                        <thead>
                            <tr>
                                <th>Task</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="tw-task-name">Build a Sentence</td>
                                <td>
                                    Put word tiles in the correct order to complete a reply in a short
                                    exchange. <strong>10 items, 6 minutes.</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="tw-task-name">Write an Email</td>
                                <td>
                                    Write an email that covers three given points, using an appropriate
                                    tone. <strong>7 minutes.</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="tw-task-name">Academic Discussion</td>
                                <td>
                                    Respond to a professor's question and classmates' posts with your
                                    own supported opinion. Aim for <strong>100+ words, 10 minutes.</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Start Button */}
                <div className="tw-btn-wrap">
                    <button
                        className="tw-btn"
                        onClick={() => navigate(mock === 1 ? "/quiz/toefl/writing" : `/quiz/toefl-mock${mock}/writing`)}
                    >
                        Start Writing Test <FaArrowRight />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ToeflWritingInstructions;